import React, { useState } from 'react';
import styled from 'styled-components';
import { Fade, Slide } from 'react-awesome-reveal';
import { PageContainer } from '@/components/layout';
import { Card, CardHeader, CardTitle, Button } from '@/components/common';
import { useAuth } from '@/context';
import { useOfficeDashboard, formatAmount } from '@/api/dashboard';
import { PendingPaymentsWidget } from '@/components/dashboard/PendingPaymentsWidget';
import { RecordExpenseModal } from '@/features/expenses/components/RecordExpenseModal';
import { RecordPaymentModal } from '@features/invoices/pages/InvoicesPage';
import { useInvoice } from '@features/invoices/api/billing';

const WelcomeBanner = styled.div`
  background: ${({ theme }) => theme.colors.cardBgGradient};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: ${({ theme }) => theme.spacing.xl};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  position: relative;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.border};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #3B82F6 0%, #60A5FA 50%, #93C5FD 100%);
  }
`;

const WelcomeTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSizes['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
  color: ${({ theme }) => theme.colors.gold};
  margin: 0 0 ${({ theme }) => theme.spacing.sm} 0;
`;

const WelcomeSubtitle = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  margin: 0;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled(Card)`
  text-align: center;
`;

const StatValue = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes['3xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
  color: ${({ theme }) => theme.colors.text};
`;

const StatLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

const QuickActionsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const QuickActionCard = styled(Card)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const OfficeDashboard: React.FC = () => {
  const { user } = useAuth();
  const { data, isLoading } = useOfficeDashboard();
  const [showRecordExpense, setShowRecordExpense] = useState(false);
  const [showRecordCash, setShowRecordCash] = useState(false);

  // "Record Cash Payment" needs a real invoice to attach the payment to —
  // pick the first pending invoice from the same payment-due data as a
  // sensible default target; the operator can pick a different invoice
  // from the Invoices page for anything not on this list.
  const firstPendingInvoiceId = data?.pending_payments.rows[0]?.invoice_id;
  const { data: targetInvoice } = useInvoice(firstPendingInvoiceId);

  return (
    <PageContainer
      title="Office Dashboard"
      subtitle="Today's collections and pending payments"
      data-testid="candeza-dashboard-page"
    >
      <Fade triggerOnce>
        <WelcomeBanner>
          <WelcomeTitle>Welcome back, {user?.email?.split('@')[0]}!</WelcomeTitle>
          <WelcomeSubtitle>Here's the front-office snapshot for today.</WelcomeSubtitle>
        </WelcomeBanner>
      </Fade>

      <Fade triggerOnce>
        <StatsGrid data-testid="candeza-dash-todays-collections">
          <StatCard padding="md">
            <StatValue>{isLoading ? '—' : formatAmount(data?.todays_collections.total ?? 0)}</StatValue>
            <StatLabel>Today's Collections</StatLabel>
          </StatCard>
          <StatCard padding="md">
            <StatValue>{isLoading ? '—' : data?.todays_collections.count ?? 0}</StatValue>
            <StatLabel>Payments Today</StatLabel>
          </StatCard>
          <StatCard padding="md">
            <StatValue>{isLoading ? '—' : formatAmount(data?.pending_payments.total_amount_due ?? 0)}</StatValue>
            <StatLabel>Total Pending</StatLabel>
          </StatCard>
        </StatsGrid>
      </Fade>

      <Slide triggerOnce direction="up">
        <QuickActionsGrid>
          <QuickActionCard padding="lg" data-testid="candeza-dash-action-record-expense-card">
            <CardHeader>
              <CardTitle>Record Expense</CardTitle>
            </CardHeader>
            <p style={{ margin: 0, color: '#94a3b8', fontSize: 14 }}>
              Log a rent, utilities, supplies, or other operational expense with a bill photo.
            </p>
            <Button variant="primary" onClick={() => setShowRecordExpense(true)} data-testid="candeza-dash-action-record-expense">
              Record Expense
            </Button>
          </QuickActionCard>

          <QuickActionCard padding="lg" data-testid="candeza-dash-action-record-cash-card">
            <CardHeader>
              <CardTitle>Record Cash Payment</CardTitle>
            </CardHeader>
            <p style={{ margin: 0, color: '#94a3b8', fontSize: 14 }}>
              Record a cash payment against an outstanding invoice, with a screenshot as proof.
            </p>
            <Button
              variant="primary"
              onClick={() => setShowRecordCash(true)}
              disabled={!targetInvoice}
              data-testid="candeza-dash-action-record-cash"
            >
              Record Cash Payment
            </Button>
            {!targetInvoice && (
              <span style={{ fontSize: 12, color: '#94a3b8' }}>
                No pending invoice to attach to — use the Invoices page directly.
              </span>
            )}
          </QuickActionCard>
        </QuickActionsGrid>
      </Slide>

      <PendingPaymentsWidget report={data?.pending_payments} isLoading={isLoading} mode="full" />

      {showRecordExpense && <RecordExpenseModal onClose={() => setShowRecordExpense(false)} />}
      {showRecordCash && targetInvoice && (
        <RecordPaymentModal invoice={targetInvoice} defaultMethod="cash" onClose={() => setShowRecordCash(false)} />
      )}
    </PageContainer>
  );
};

export default OfficeDashboard;
